import {
  collection,
  doc,
  setDoc,
  getDoc,
  onSnapshot,
  Timestamp,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/api/firebase/firebase";

export type TransactionViewMode = "default" | "custom";

export interface UserProgressState {
  userId: string;
  cohortId: string;
  companyName?: string;
  // Company name used with the custom transaction set; kept apart from the
  // primary one so each set has its own.
  customCompanyName?: string;
  currentTransactionIndex: number;
  // Tracks the highest transaction index the user has visited (for revealing in summary)
  highestTransactionIndex?: number;
  // Tracks which view mode (default/custom) the user is currently using
  viewMode?: TransactionViewMode;
  // For tracking custom transactions separately
  customTransactionIndex?: number;
  // Tracks the highest custom transaction index the user has visited
  customHighestTransactionIndex?: number;
  accounts: Array<{
    id: string;
    name: string;
    balance: number;
    label?: string;
    comment?: string;
  }>;
  liabilities: Array<{
    id: string;
    name: string;
    balance: number;
    label?: string;
    comment?: string;
  }>;
  equityAccounts: Array<{
    id: string;
    name: string;
    balance: number;
    label?: string;
    comment?: string;
  }>;
  equityAmounts: Array<{
    id: string;
    amount: number;
    formattedAmount: string;
    transactionId: number;
    type: string;
    originalColor?: string;
    isPositive?: boolean;
  }>;
  accountDroppedAmounts: Record<
    string,
    Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>
  >;
  liabilityDroppedAmounts: Record<
    string,
    Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>
  >;
  equityAccountDroppedAmounts: Record<
    string,
    Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>
  >;
  expenseAccounts?: Array<{
    id: string;
    name: string;
    balance: number;
    label?: string;
    comment?: string;
  }>;
  incomeAccounts?: Array<{
    id: string;
    name: string;
    balance: number;
    label?: string;
    comment?: string;
  }>;
  expenseDroppedAmounts?: Record<
    string,
    Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>
  >;
  incomeDroppedAmounts?: Record<
    string,
    Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>
  >;
  // For storing custom transaction view data
  customAccounts?: Array<{
    id: string;
    name: string;
    balance: number;
    label?: string;
    comment?: string;
  }>;
  customLiabilities?: Array<{
    id: string;
    name: string;
    balance: number;
    label?: string;
    comment?: string;
  }>;
  customEquityAccounts?: Array<{
    id: string;
    name: string;
    balance: number;
    label?: string;
    comment?: string;
  }>;
  customEquityAmounts?: Array<{
    id: string;
    amount: number;
    formattedAmount: string;
    transactionId: number;
    type: string;
    originalColor?: string;
    isPositive?: boolean;
  }>;
  customAccountDroppedAmounts?: Record<
    string,
    Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>
  >;
  customLiabilityDroppedAmounts?: Record<
    string,
    Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>
  >;
  customEquityAccountDroppedAmounts?: Record<
    string,
    Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>
  >;
  customExpenseAccounts?: Array<{
    id: string;
    name: string;
    balance: number;
    label?: string;
    comment?: string;
  }>;
  customIncomeAccounts?: Array<{
    id: string;
    name: string;
    balance: number;
    label?: string;
    comment?: string;
  }>;
  customExpenseDroppedAmounts?: Record<
    string,
    Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>
  >;
  customIncomeDroppedAmounts?: Record<
    string,
    Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>
  >;
  transactionNotes?: Record<number, string>;
  // Notes for the custom transaction set. Both sets number their transactions
  // from 1, so they can't share one map without overwriting each other.
  customTransactionNotes?: Record<number, string>;
  sectionHints?: Record<string, string>;
  lastUpdated: Timestamp;
}

export class UserProgressService {
  private static readonly COLLECTION_NAME = "userProgress";
  private static readonly USERS_COLLECTION = "users";

  /**
   * Merge account arrays, preserving names/labels/comments from existing if incoming doesn't have them.
   * This prevents race conditions where saveProgress overwrites data that was just saved by updateAccounts.
   * CRITICAL: If incoming is empty, return existing data to prevent data loss!
   */
  private static mergeAccountArrays(
    incoming: any[] = [],
    existing: any[] = [],
  ): any[] {
    // If incoming is empty but existing has data, preserve existing!
    // This prevents race conditions where stale state overwrites real data
    if (incoming.length === 0 && existing.length > 0) {
      return existing;
    }

    const existingById = new Map(existing.map((a) => [a.id, a]));
    return incoming.map((acc) => {
      const existingAcc = existingById.get(acc.id);
      const incomingName = (acc.name ?? "").trim();
      // For label, treat empty string same as undefined - use existing if incoming is empty/null/undefined
      const incomingLabel =
        acc.label && acc.label.trim() !== "" ? acc.label : null;
      return {
        ...acc,
        // Preserve existing name if incoming is empty
        name:
          incomingName.length === 0 && existingAcc?.name
            ? existingAcc.name
            : acc.name,
        // Preserve existing label if incoming doesn't have one or is empty
        label: incomingLabel ?? existingAcc?.label ?? null,
        // Preserve existing comment if incoming doesn't have one
        comment: acc.comment ?? existingAcc?.comment ?? null,
      };
    });
  }

  static async saveUserProgress(
    userId: string,
    cohortId: string,
    progressData: Omit<
      UserProgressState,
      "userId" | "cohortId" | "lastUpdated"
    >,
  ): Promise<void> {
    try {
      const docId = `${userId}_${cohortId}`;
      const progressDoc = doc(db, this.COLLECTION_NAME, docId);

      // Read existing data to merge account arrays properly
      const existingSnap = await getDoc(progressDoc);
      const existingData = existingSnap.exists()
        ? (existingSnap.data() as UserProgressState)
        : null;

      // Merge account arrays to prevent stale data from overwriting names/labels
      const mergedProgressData = {
        ...progressData,
        accounts: this.mergeAccountArrays(
          progressData.accounts || [],
          existingData?.accounts || [],
        ),
        liabilities: this.mergeAccountArrays(
          progressData.liabilities || [],
          existingData?.liabilities || [],
        ),
        equityAccounts: this.mergeAccountArrays(
          progressData.equityAccounts || [],
          existingData?.equityAccounts || [],
        ),
        expenseAccounts: this.mergeAccountArrays(
          progressData.expenseAccounts || [],
          existingData?.expenseAccounts || [],
        ),
        incomeAccounts: this.mergeAccountArrays(
          progressData.incomeAccounts || [],
          existingData?.incomeAccounts || [],
        ),
        // Also merge custom view accounts
        customAccounts: this.mergeAccountArrays(
          progressData.customAccounts || [],
          existingData?.customAccounts || [],
        ),
        customLiabilities: this.mergeAccountArrays(
          progressData.customLiabilities || [],
          existingData?.customLiabilities || [],
        ),
        customEquityAccounts: this.mergeAccountArrays(
          progressData.customEquityAccounts || [],
          existingData?.customEquityAccounts || [],
        ),
        customExpenseAccounts: this.mergeAccountArrays(
          progressData.customExpenseAccounts || [],
          existingData?.customExpenseAccounts || [],
        ),
        customIncomeAccounts: this.mergeAccountArrays(
          progressData.customIncomeAccounts || [],
          existingData?.customIncomeAccounts || [],
        ),
      };

      const progressState: UserProgressState = {
        userId,
        cohortId,
        ...mergedProgressData,
        lastUpdated: Timestamp.now(),
      };

      await setDoc(progressDoc, progressState, { merge: true });
    } catch (error) {
      console.error("Error saving user progress:", error);
      throw new Error("Failed to save user progress");
    }
  }

  // Safely update a single account's name without relying on potentially stale arrays in memory
  static async updateAccountName(
    userId: string,
    cohortId: string,
    accountType: "assets" | "liabilities" | "equity" | "expenses" | "income",
    accountId: string,
    newName: string,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const docSnap = await getDoc(progressRef);
      if (!docSnap.exists()) {
        throw new Error("User progress not found");
      }

      const data = docSnap.data() as UserProgressState;

      // Determine the correct field to read and write
      const fieldMap: Record<
        typeof accountType,
        { default: keyof UserProgressState; custom: keyof UserProgressState }
      > = {
        assets: { default: "accounts", custom: "customAccounts" },
        liabilities: { default: "liabilities", custom: "customLiabilities" },
        equity: { default: "equityAccounts", custom: "customEquityAccounts" },
        expenses: {
          default: "expenseAccounts",
          custom: "customExpenseAccounts",
        },
        income: { default: "incomeAccounts", custom: "customIncomeAccounts" },
      };

      const targetKey =
        viewMode === "custom"
          ? fieldMap[accountType].custom
          : fieldMap[accountType].default;

      const currentList =
        (data[targetKey] as Array<{
          id: string;
          name: string;
          balance: number;
        }>) || [];

      const updatedList = currentList.map((acc) =>
        acc.id === accountId ? { ...acc, name: newName } : acc,
      );

      await setDoc(
        progressRef,
        {
          [targetKey]: updatedList,
          lastUpdated: Timestamp.now(),
        } as any,
        { merge: true },
      );
    } catch (error) {
      console.error("Error updating account name:", error);
      throw error;
    }
  }

  static async getUserProgress(
    userId: string,
    cohortId: string,
  ): Promise<UserProgressState | null> {
    try {
      const docId = `${userId}_${cohortId}`;
      const progressDoc = doc(db, this.COLLECTION_NAME, docId);
      const docSnap = await getDoc(progressDoc);

      if (docSnap.exists()) {
        const data = docSnap.data() as UserProgressState;
        return data;
      } else {
        return null;
      }
    } catch (error) {
      console.error("❌ Error getting user progress:", error);
      throw new Error("Failed to get user progress");
    }
  }

  static subscribeToUserProgress(
    userId: string,
    cohortId: string,
    callback: (progress: UserProgressState | null) => void,
  ): () => void {
    const docId = `${userId}_${cohortId}`;
    const progressDoc = doc(db, this.COLLECTION_NAME, docId);

    const unsubscribe = onSnapshot(progressDoc, (doc) => {
      if (doc.exists()) {
        callback(doc.data() as UserProgressState);
      } else {
        callback(null);
      }
    });

    return unsubscribe;
  }

  static async getAllCohortProgress(
    cohortId: string,
  ): Promise<UserProgressState[]> {
    try {
      // Since we can't query by partial document ID, we'll get all progress docs
      // and filter by cohortId. In a production app, you might want to restructure this.
      const progressCollection = collection(db, this.COLLECTION_NAME);

      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(
          progressCollection,
          (snapshot) => {
            const progressData: UserProgressState[] = [];

            snapshot.forEach((doc) => {
              const data = doc.data() as UserProgressState;
              if (data.cohortId === cohortId) {
                progressData.push(data);
              }
            });

            resolve(progressData);
            unsubscribe();
          },
          (error) => {
            reject(error);
          },
        );
      });
    } catch (error) {
      console.error("Error getting cohort progress:", error);
      throw new Error("Failed to get cohort progress");
    }
  }

  static subscribeToAllCohortProgress(
    cohortId: string,
    callback: (progressList: UserProgressState[]) => void,
  ): () => void {
    const progressCollection = collection(db, this.COLLECTION_NAME);

    const unsubscribe = onSnapshot(progressCollection, (snapshot) => {
      const progressData: UserProgressState[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data() as UserProgressState;
        if (data.cohortId === cohortId) {
          progressData.push(data);
        }
      });

      callback(progressData);
    });

    return unsubscribe;
  }

  static async getUserIdByEmail(email: string): Promise<string | null> {
    try {
      // Firestore equality is case-sensitive, but admins type participant
      // emails with any casing while user docs usually store them lowercase.
      // Try the email as given, then lowercase, then the emailLowercase field,
      // so trainer progress views don't show "No activity yet" for a casing
      // mismatch.
      const usersCollection = collection(db, this.USERS_COLLECTION);
      const trimmed = (email || "").trim();
      const normalized = trimmed.toLowerCase();
      const candidates: Array<[string, string]> = [["email", trimmed]];
      if (normalized !== trimmed) candidates.push(["email", normalized]);
      candidates.push(["emailLowercase", normalized]);

      for (const [field, value] of candidates) {
        if (!value) continue;
        const querySnapshot = await getDocs(
          query(usersCollection, where(field, "==", value)),
        );
        if (!querySnapshot.empty) {
          return querySnapshot.docs[0].id;
        }
      }
      return null;
    } catch (error) {
      console.error("Error finding user by email:", error);
      return null;
    }
  }

  static async getUserProgressByEmail(
    email: string,
    cohortId: string,
  ): Promise<UserProgressState | null> {
    try {
      const userId = await this.getUserIdByEmail(email);
      if (!userId) {
        return null;
      }

      return await this.getUserProgress(userId, cohortId);
    } catch (error) {
      console.error("Error getting user progress by email:", error);
      return null;
    }
  }

  static subscribeToUserProgressByEmail(
    email: string,
    cohortId: string,
    callback: (progress: UserProgressState | null) => void,
  ): () => void {
    let unsubscribe: () => void = () => {};

    const setupSubscription = async () => {
      const userId = await this.getUserIdByEmail(email);
      if (userId) {
        unsubscribe = this.subscribeToUserProgress(
          userId,
          cohortId,
          (progress) => {
            callback(progress);
          },
        );
      } else {
        callback(null);
      }
    };

    setupSubscription();

    return () => {
      unsubscribe();
    };
  }

  // Update the user's view mode (default or custom)
  static async updateUserViewMode(
    userId: string,
    cohortId: string,
    viewMode: TransactionViewMode,
  ): Promise<void> {
    try {
      const docId = `${userId}_${cohortId}`;
      const progressDoc = doc(db, this.COLLECTION_NAME, docId);

      await setDoc(
        progressDoc,
        {
          userId,
          cohortId,
          viewMode,
          lastUpdated: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Error updating user view mode:", error);
      throw new Error("Failed to update user view mode");
    }
  }

  // Get the current transaction index based on view mode
  static getCurrentTransactionIndex(
    progress: UserProgressState,
    viewMode: TransactionViewMode = "default",
  ): number {
    if (!progress) return 0;

    if (viewMode === "custom") {
      return progress.customTransactionIndex || 0;
    } else {
      return progress.currentTransactionIndex || 0;
    }
  }

  // Reset user progress to initial state
  static async resetUserProgress(
    userId: string,
    cohortId: string,
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      // Create an empty progress object with just the essential fields
      const emptyProgress = {
        userId,
        cohortId,
        currentTransactionIndex: 0,
        highestTransactionIndex: 0,
        customTransactionIndex: 0,
        customHighestTransactionIndex: 0,
        accounts: [],
        liabilities: [],
        equityAccounts: [],
        equityAmounts: [],
        accountDroppedAmounts: {},
        liabilityDroppedAmounts: {},
        equityAccountDroppedAmounts: {},
        expenseAccounts: [],
        incomeAccounts: [],
        expenseDroppedAmounts: {},
        incomeDroppedAmounts: {},
        customAccounts: [],
        customLiabilities: [],
        customEquityAccounts: [],
        customEquityAmounts: [],
        customAccountDroppedAmounts: {},
        customLiabilityDroppedAmounts: {},
        customEquityAccountDroppedAmounts: {},
        customExpenseAccounts: [],
        customIncomeAccounts: [],
        customExpenseDroppedAmounts: {},
        customIncomeDroppedAmounts: {},
        lastUpdated: Timestamp.now(),
      };

      await setDoc(progressRef, emptyProgress);
    } catch (error) {
      console.error("Error resetting user progress:", error);
      throw error;
    }
  }

  // Update company name
  static async updateCompanyName(
    userId: string,
    cohortId: string,
    companyName: string,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      // Get the current progress first
      const progressDoc = await getDoc(progressRef);
      if (!progressDoc.exists()) {
        throw new Error("User progress not found");
      }

      // Update only the company name field for this transaction set
      await setDoc(
        progressRef,
        {
          [viewMode === "custom" ? "customCompanyName" : "companyName"]:
            companyName,
          lastUpdated: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Error updating company name:", error);
      throw error;
    }
  }

  // Update transaction index
  static async updateTransactionIndex(
    userId: string,
    cohortId: string,
    index: number,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const updateField =
        viewMode === "custom"
          ? "customTransactionIndex"
          : "currentTransactionIndex";

      const highestField =
        viewMode === "custom"
          ? "customHighestTransactionIndex"
          : "highestTransactionIndex";

      // Get current highest to compare
      const currentDoc = await getDoc(progressRef);
      const currentData = currentDoc.exists()
        ? (currentDoc.data() as UserProgressState)
        : null;
      const currentHighest =
        (currentData?.[highestField as keyof UserProgressState] as
          | number
          | undefined) ?? 0;

      // Build update object - always update current index, only update highest if new index is greater
      const updateData: Record<string, unknown> = {
        [updateField]: index,
        lastUpdated: Timestamp.now(),
      };

      // Update highest if navigating to a higher transaction
      if (index > currentHighest) {
        updateData[highestField] = index;
      }

      // Update the transaction index
      await setDoc(progressRef, updateData, { merge: true });
    } catch (error) {
      console.error(`Error updating ${viewMode} transaction index:`, error);
      throw error;
    }
  }

  // Generic update method for accounts
  static async updateAccounts(
    userId: string,
    cohortId: string,
    accounts: Array<{
      id: string;
      name: string;
      balance: number;
      label?: string;
      comment?: string;
    }>,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field = viewMode === "custom" ? "customAccounts" : "accounts";

      // Merge with current stored list to avoid blank-name overwrites
      const snap = await getDoc(progressRef);
      const current = (
        snap.exists()
          ? (snap.data() as UserProgressState)[field as keyof UserProgressState]
          : []
      ) as Array<{
        id: string;
        name: string;
        balance: number;
        label?: string;
        comment?: string;
      }>;

      const currentById = new Map(current.map((a) => [a.id, a]));
      const merged = accounts.map((incoming) => {
        const existing = currentById.get(incoming.id);
        const incomingName = (incoming.name ?? "").trim();
        // For label, treat empty string same as undefined - use existing if incoming is empty/null/undefined
        const incomingLabel =
          incoming.label && incoming.label.trim() !== ""
            ? incoming.label
            : null;
        const next = {
          ...incoming,
          name:
            incomingName.length === 0 && existing?.name
              ? existing.name
              : incoming.name,
          label: incomingLabel ?? existing?.label ?? null,
          comment: incoming.comment ?? existing?.comment ?? null,
        } as any;
        // Firestore: avoid undefined values
        if (next.label === undefined) delete next.label;
        if (next.comment === undefined) delete next.comment;
        return next;
      });

      await setDoc(
        progressRef,
        {
          [field]: merged,
          lastUpdated: Timestamp.now(),
        } as any,
        { merge: true },
      );
    } catch (error) {
      console.error(`Error updating ${viewMode} accounts:`, error);
      throw error;
    }
  }

  // Generic update method for liabilities
  static async updateLiabilities(
    userId: string,
    cohortId: string,
    liabilities: Array<{
      id: string;
      name: string;
      balance: number;
      label?: string;
      comment?: string;
    }>,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field = viewMode === "custom" ? "customLiabilities" : "liabilities";

      const snap = await getDoc(progressRef);
      const current = (
        snap.exists()
          ? (snap.data() as UserProgressState)[field as keyof UserProgressState]
          : []
      ) as Array<{
        id: string;
        name: string;
        balance: number;
        label?: string;
        comment?: string;
      }>;
      const currentById = new Map(current.map((a) => [a.id, a]));
      const merged = liabilities.map((incoming) => {
        const existing = currentById.get(incoming.id);
        const incomingName = (incoming.name ?? "").trim();
        const incomingLabel =
          incoming.label && incoming.label.trim() !== ""
            ? incoming.label
            : null;
        const next = {
          ...incoming,
          name:
            incomingName.length === 0 && existing?.name
              ? existing.name
              : incoming.name,
          label: incomingLabel ?? existing?.label ?? null,
          comment: incoming.comment ?? existing?.comment ?? null,
        } as any;
        if (next.label === undefined) delete next.label;
        if (next.comment === undefined) delete next.comment;
        return next;
      });

      await setDoc(
        progressRef,
        {
          [field]: merged,
          lastUpdated: Timestamp.now(),
        } as any,
        { merge: true },
      );
    } catch (error) {
      console.error(`Error updating ${viewMode} liabilities:`, error);
      throw error;
    }
  }

  // Generic update method for equity accounts
  static async updateEquityAccounts(
    userId: string,
    cohortId: string,
    equityAccounts: Array<{
      id: string;
      name: string;
      balance: number;
      label?: string;
      comment?: string;
    }>,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field =
        viewMode === "custom" ? "customEquityAccounts" : "equityAccounts";

      const snap = await getDoc(progressRef);
      const current = (
        snap.exists()
          ? (snap.data() as UserProgressState)[field as keyof UserProgressState]
          : []
      ) as Array<{
        id: string;
        name: string;
        balance: number;
        label?: string;
        comment?: string;
      }>;
      const currentById = new Map(current.map((a) => [a.id, a]));
      const merged = equityAccounts.map((incoming) => {
        const existing = currentById.get(incoming.id);
        const incomingName = (incoming.name ?? "").trim();
        const incomingLabel =
          incoming.label && incoming.label.trim() !== ""
            ? incoming.label
            : null;
        const next = {
          ...incoming,
          name:
            incomingName.length === 0 && existing?.name
              ? existing.name
              : incoming.name,
          label: incomingLabel ?? existing?.label ?? null,
          comment: incoming.comment ?? existing?.comment ?? null,
        } as any;
        if (next.label === undefined) delete next.label;
        if (next.comment === undefined) delete next.comment;
        return next;
      });

      await setDoc(
        progressRef,
        {
          [field]: merged,
          lastUpdated: Timestamp.now(),
        } as any,
        { merge: true },
      );
    } catch (error) {
      console.error(`Error updating ${viewMode} equity accounts:`, error);
      throw error;
    }
  }

  // Generic update method for equity amounts
  static async updateEquityAmounts(
    userId: string,
    cohortId: string,
    equityAmounts: Array<{
      id: string;
      amount: number;
      formattedAmount: string;
      transactionId: number;
      type: string;
      originalColor?: string;
      isPositive?: boolean;
    }>,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field =
        viewMode === "custom" ? "customEquityAmounts" : "equityAmounts";

      await setDoc(
        progressRef,
        {
          [field]: equityAmounts,
          lastUpdated: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error(`Error updating ${viewMode} equity amounts:`, error);
      throw error;
    }
  }

  // Generic update method for account dropped amounts
  static async updateAccountDroppedAmounts(
    userId: string,
    cohortId: string,
    droppedAmounts: Record<
      string,
      Array<{
        id: string;
        amount: number;
        formattedAmount: string;
        transactionId: number;
        type: string;
        originalColor?: string;
        isPositive?: boolean;
      }>
    >,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field =
        viewMode === "custom"
          ? "customAccountDroppedAmounts"
          : "accountDroppedAmounts";

      await setDoc(
        progressRef,
        {
          [field]: droppedAmounts,
          lastUpdated: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error(
        `Error updating ${viewMode} account dropped amounts:`,
        error,
      );
      throw error;
    }
  }

  // Generic update method for liability dropped amounts
  static async updateLiabilityDroppedAmounts(
    userId: string,
    cohortId: string,
    droppedAmounts: Record<
      string,
      Array<{
        id: string;
        amount: number;
        formattedAmount: string;
        transactionId: number;
        type: string;
        originalColor?: string;
        isPositive?: boolean;
      }>
    >,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field =
        viewMode === "custom"
          ? "customLiabilityDroppedAmounts"
          : "liabilityDroppedAmounts";

      await setDoc(
        progressRef,
        {
          [field]: droppedAmounts,
          lastUpdated: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error(
        `Error updating ${viewMode} liability dropped amounts:`,
        error,
      );
      throw error;
    }
  }

  // Generic update method for equity account dropped amounts
  static async updateEquityAccountDroppedAmounts(
    userId: string,
    cohortId: string,
    droppedAmounts: Record<
      string,
      Array<{
        id: string;
        amount: number;
        formattedAmount: string;
        transactionId: number;
        type: string;
        originalColor?: string;
        isPositive?: boolean;
      }>
    >,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field =
        viewMode === "custom"
          ? "customEquityAccountDroppedAmounts"
          : "equityAccountDroppedAmounts";

      await setDoc(
        progressRef,
        {
          [field]: droppedAmounts,
          lastUpdated: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error(
        `Error updating ${viewMode} equity account dropped amounts:`,
        error,
      );
      throw error;
    }
  }

  // Generic update method for expense accounts
  static async updateExpenseAccounts(
    userId: string,
    cohortId: string,
    expenseAccounts: Array<{
      id: string;
      name: string;
      balance: number;
      label?: string;
      comment?: string;
    }>,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field =
        viewMode === "custom" ? "customExpenseAccounts" : "expenseAccounts";

      const snap = await getDoc(progressRef);
      const current = (
        snap.exists()
          ? (snap.data() as UserProgressState)[field as keyof UserProgressState]
          : []
      ) as Array<{
        id: string;
        name: string;
        balance: number;
        label?: string;
        comment?: string;
      }>;
      const currentById = new Map(current.map((a) => [a.id, a]));
      const merged = expenseAccounts.map((incoming) => {
        const existing = currentById.get(incoming.id);
        const incomingName = (incoming.name ?? "").trim();
        const incomingLabel =
          incoming.label && incoming.label.trim() !== ""
            ? incoming.label
            : null;
        const next = {
          ...incoming,
          name:
            incomingName.length === 0 && existing?.name
              ? existing.name
              : incoming.name,
          label: incomingLabel ?? existing?.label ?? null,
          comment: incoming.comment ?? existing?.comment ?? null,
        } as any;
        if (next.label === undefined) delete next.label;
        if (next.comment === undefined) delete next.comment;
        return next;
      });

      await setDoc(
        progressRef,
        {
          [field]: merged,
          lastUpdated: Timestamp.now(),
        } as any,
        { merge: true },
      );
    } catch (error) {
      console.error(`Error updating ${viewMode} expense accounts:`, error);
      throw error;
    }
  }

  // Generic update method for income accounts
  static async updateIncomeAccounts(
    userId: string,
    cohortId: string,
    incomeAccounts: Array<{
      id: string;
      name: string;
      balance: number;
      label?: string;
      comment?: string;
    }>,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field =
        viewMode === "custom" ? "customIncomeAccounts" : "incomeAccounts";

      const snap = await getDoc(progressRef);
      const current = (
        snap.exists()
          ? (snap.data() as UserProgressState)[field as keyof UserProgressState]
          : []
      ) as Array<{
        id: string;
        name: string;
        balance: number;
        label?: string;
        comment?: string;
      }>;
      const currentById = new Map(current.map((a) => [a.id, a]));
      const merged = incomeAccounts.map((incoming) => {
        const existing = currentById.get(incoming.id);
        const incomingName = (incoming.name ?? "").trim();
        const incomingLabel =
          incoming.label && incoming.label.trim() !== ""
            ? incoming.label
            : null;
        const next = {
          ...incoming,
          name:
            incomingName.length === 0 && existing?.name
              ? existing.name
              : incoming.name,
          label: incomingLabel ?? existing?.label ?? null,
          comment: incoming.comment ?? existing?.comment ?? null,
        } as any;
        if (next.label === undefined) delete next.label;
        if (next.comment === undefined) delete next.comment;
        return next;
      });

      await setDoc(
        progressRef,
        {
          [field]: merged,
          lastUpdated: Timestamp.now(),
        } as any,
        { merge: true },
      );
    } catch (error) {
      console.error(`Error updating ${viewMode} income accounts:`, error);
      throw error;
    }
  }

  // Generic update method for expense dropped amounts
  static async updateExpenseDroppedAmounts(
    userId: string,
    cohortId: string,
    droppedAmounts: Record<
      string,
      Array<{
        id: string;
        amount: number;
        formattedAmount: string;
        transactionId: number;
        type: string;
        originalColor?: string;
        isPositive?: boolean;
      }>
    >,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field =
        viewMode === "custom"
          ? "customExpenseDroppedAmounts"
          : "expenseDroppedAmounts";

      await setDoc(
        progressRef,
        {
          [field]: droppedAmounts,
          lastUpdated: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error(
        `Error updating ${viewMode} expense dropped amounts:`,
        error,
      );
      throw error;
    }
  }

  // Generic update method for income dropped amounts
  static async updateIncomeDroppedAmounts(
    userId: string,
    cohortId: string,
    droppedAmounts: Record<
      string,
      Array<{
        id: string;
        amount: number;
        formattedAmount: string;
        transactionId: number;
        type: string;
        originalColor?: string;
        isPositive?: boolean;
      }>
    >,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const progressRef = doc(
        db,
        this.COLLECTION_NAME,
        `${userId}_${cohortId}`,
      );

      const field =
        viewMode === "custom"
          ? "customIncomeDroppedAmounts"
          : "incomeDroppedAmounts";

      await setDoc(
        progressRef,
        {
          [field]: droppedAmounts,
          lastUpdated: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error(
        `Error updating ${viewMode} income dropped amounts:`,
        error,
      );
      throw error;
    }
  }

  static async saveTransactionNote(
    userId: string,
    cohortId: string,
    transactionId: number,
    note: string,
    viewMode: TransactionViewMode = "default",
  ): Promise<void> {
    try {
      const docId = `${userId}_${cohortId}`;
      const progressDoc = doc(db, this.COLLECTION_NAME, docId);
      const field =
        viewMode === "custom" ? "customTransactionNotes" : "transactionNotes";

      // Get current progress to merge with existing notes
      const currentDoc = await getDoc(progressDoc);
      const currentData = currentDoc.data() as UserProgressState;

      const updatedNotes = {
        ...currentData?.[field],
        [transactionId]: note,
      };

      await setDoc(
        progressDoc,
        {
          [field]: updatedNotes,
          lastUpdated: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error("❌ Firebase - Error saving transaction note:", error);
      throw error;
    }
  }

  static async getTransactionNote(
    userId: string,
    cohortId: string,
    transactionId: number,
    viewMode: TransactionViewMode = "default",
  ): Promise<string | null> {
    try {
      const docId = `${userId}_${cohortId}`;
      const progressDoc = doc(db, this.COLLECTION_NAME, docId);

      const docSnap = await getDoc(progressDoc);
      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data() as UserProgressState;
      const notes =
        viewMode === "custom"
          ? data.customTransactionNotes
          : data.transactionNotes;
      return notes?.[transactionId] || null;
    } catch (error) {
      console.error("Error getting transaction note:", error);
      throw error;
    }
  }

  static async updateSectionHint(
    userId: string,
    cohortId: string,
    sectionType: string,
    hint: string,
  ): Promise<void> {
    try {
      const docId = `${userId}_${cohortId}`;
      const progressDoc = doc(db, this.COLLECTION_NAME, docId);

      const currentDoc = await getDoc(progressDoc);
      const currentData = currentDoc.data() as UserProgressState;

      const updatedHints = {
        ...currentData?.sectionHints,
        [sectionType]: hint,
      };

      await setDoc(
        progressDoc,
        {
          sectionHints: updatedHints,
          lastUpdated: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Error saving section hint:", error);
      throw error;
    }
  }
}
